import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';

import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {LoaderService} from '../../../../services/loader.service';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {ToolsService} from '../../../../services/tools.service';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {WpService} from '../../../../services/wp.service';
import {MetaTag} from '../../../../types/MetaTag';
import {WPUser} from '../../../../types/WPUser';
import {MediaSizeEnum} from '../../../../enums/WPEnums';
import {AuthorUrlResolverService} from '../../services/author-url-resolver.service';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {AppConfigService} from '../../../../services/app-config.service';
import {StaticSidebarPuffsService} from '../../../../services/static-sidebar-puffs.service';
import {StaticAuthorService} from '../../services/static-author.service';

@Component({
  selector: 'app-author',
  templateUrl: './author.component.html',
  styleUrls: ['./author.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthorComponent implements OnInit, OnDestroy {
  public authorData: WPUser;
  public mediaSizeEnum = MediaSizeEnum;
  public pageType: DLPageTypeEnum = DLPageTypeEnum.OTHER_PAGE;
  private urlPath = '';
  private subscriptions: Subscription[] = [];
  private pageTitle: string;

  constructor(
    private authorService: StaticAuthorService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private blogTools: WpService,
    private authorUrlResolver: AuthorUrlResolverService,
    private appConfigService: AppConfigService,
    private _change: ChangeDetectorRef,
    private sidebarPuffsService: StaticSidebarPuffsService,
  ) {}

  ngOnInit() {
    this.sidebarPuffsService.puffs.set([]);
    const isRedirected: boolean = this.authorUrlResolver.cleanUpUrl(this.route.snapshot);
    this.urlPath = this.route.snapshot.params.urlPath;
    this.loadData(isRedirected);

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const isRedirected: boolean = this.authorUrlResolver.cleanUpUrl(this.route.snapshot);
          this.urlPath = this.route.snapshot.params.urlPath;
          this.loadData(isRedirected);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  scrollToTop(): void {
    this.tools.scrollTo();
  }

  private loadData(isRedirected: boolean): void {
    if (!this.tools.isNil(this.authorData) && this.authorData.slug === this.urlPath) {
      this.processDataResults(this.authorData, isRedirected);
    } else {
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);

      this.subscriptions.push(
        this.authorService
          .GetAuthorBySlug(this.urlPath)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processDataResults(result, isRedirected),
            (error) => {
              console.error(error);
              this.onDataLoadingFinish();
            },
          ),
      );
    }
  }

  private processDataResults(result: WPUser, isRedirected: boolean): void {
    this.authorData = result;
    this.updateMetaTags();
    this.scrollToTop();
    if (isRedirected === false) {
      const customGa4Params: DataLayerParamsPageViewGA4 = {
        pageTitle: this.pageTitle,
        pageType: DLPageTypeEnum.OTHER_PAGE,
      };
      this.pageViewGa4Resolver.trackPageView(customGa4Params);
    }
    this.onDataLoadingFinish();
  }

  private onDataLoadingFinish(): void {
    this._change.markForCheck();
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const page: WPUser = this.authorData;

    if (!this.tools.isNil(page) && !this.tools.isNil(page.acf)) {
      const title: string = page.acf.seoTitle;
      const description: string = page.acf.seoDescription;
      this.pageTitle = title;

      metaTags.push({
        value: title,
        type: 'title',
        field: 'title',
      });

      metaTags.push({
        value: title,
        type: 'property',
        field: 'og:title',
      });

      metaTags.push({
        value: 'website',
        type: 'property',
        field: 'og:type',
      });

      metaTags.push({
        value: this.urlBuilder.CreateStaticLink(this.urlBuilder.CreateAuthorRouterLink(this.urlPath)),
        type: 'property',
        field: 'og:url',
      });

      metaTags.push({
        value: description,
        type: 'name',
        field: 'description',
      });

      metaTags.push({
        value: description,
        type: 'property',
        field: 'og:description',
      });

      metaTags.push({
        value: this.appConfigService.appConfig.siteName,
        type: 'property',
        field: 'og:site_name',
      });

      if (!this.tools.isNil(this.authorData.acf.mainImage)) {
        metaTags.push({
          value: this.blogTools.getImageFlatUrl(this.authorData.acf.mainImage),
          type: 'property',
          field: 'og:image',
        });

        metaTags.push({
          value: this.blogTools.getImageFlatUrl(this.authorData.acf.mainImage),
          type: 'name',
          field: 'twitter:image',
        });

        metaTags.push({
          value: 'summary_large_image',
          type: 'name',
          field: 'twitter:card',
        });
      }

      this.metaService.updateMetaTags(metaTags);
    }
  }
}
